import { DateTime } from "luxon";
import { Button, ButtonProps } from "../../Button";
import { useCalendar } from "../CalendarContext";
import { MouseEvent, forwardRef } from "react";
import {
  isBeyondMaxDate,
  isBeyondMinDate,
  isUnavailableDate,
  rangeUpdater,
  toDateString,
} from "../utils";

export type CalendarNowButtonProps = {
  /**
   * @deprecated This is overridden internally
   */
  onClick?: (e: MouseEvent<HTMLButtonElement>, date: string | null) => void;
} & Omit<ButtonProps, "onClick">;

/**
 * A button that displays the current date and allows the user to select it.
 *
 * @param props - The rest of the props for the Button component.
 * @returns A button that displays the current date and allows the user to select it.
 */
export const CalendarNowButton = forwardRef<
  HTMLButtonElement,
  CalendarNowButtonProps
>((props, ref) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- filtering out onClick
  const { onClick, ...rest } = props;
  const {
    setFocusedDate,
    setSelectedMonth,
    setSelectedYear,
    value,
    setValue,
    defaultTimeZone,
    unavailableDates,
    unavailable,
    onSelection,
    minDate,
    maxDate,
    controlled,
    range,
  } = useCalendar();

  const nToday = DateTime.fromISO(DateTime.now().toISO(), {
    zone: defaultTimeZone,
  }).startOf("day");
  const onClickHandler = () => {
    setFocusedDate(nToday);
    setSelectedMonth(nToday.month);
    setSelectedYear(nToday.year);
    if (!range) {
      onSelection?.({
        value: toDateString(nToday) ?? undefined,
      });
      if (!controlled) {
        setValue(nToday);
      }
      return;
    }
    rangeUpdater(value, nToday, setValue, onSelection);
  };

  const isDisabled =
    isBeyondMaxDate(nToday, maxDate) ||
    isBeyondMinDate(nToday, minDate) ||
    isUnavailableDate(nToday, unavailableDates, unavailable, defaultTimeZone);

  return (
    <Button
      ref={ref}
      onClick={onClickHandler}
      appearance="ghost"
      disabled={isDisabled}
      data-anv="calendar-now"
      size="small"
      {...rest}
    >
      Today
    </Button>
  );
});

CalendarNowButton.displayName = "CalendarNowButton";
